import {
  ComponentMultiStyleConfig,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { ReactElement, useMemo } from "react";
import { ComponentStyleProps } from "../../../lib/styles/props/component-style-props";
import { FiCalendar } from "react-icons/fi";
import "react-datepicker/dist/react-datepicker.css";
import { CMDatePicker } from "../../base";
import { isEqual } from "lodash";
import { ymdToText } from "../../../lib/util/common-date-util";
import { hasValue } from "../../../lib/util/common-util";

export type CMFormInputBaseDateProps = {
  editMode?: "editable" | "disabled" | "readOnly";
  variant?: "default" | "error" | "diff";
  size?: "sm" | "md" | "lg";

  /**
   * 値
   */
  value?: { year: number; month: number; day: number } | null;

  /**
   * 差分モード時の変更前の値。
   * 差分モードの場合のみ必須。
   */
  changedFrom?: { year: number; month: number; day: number } | null;

  icon?: ReactElement;
  placeholder?: string;

  /**
   * 選択可能な一番古い日付。
   */
  minDate?: { year: number; month: number; day: number };

  /**
   * 選択可能な一番新しい日付。
   *
   */
  maxDate?: { year: number; month: number; day: number };

  /**
   * 呼び出し元に年月情報を返却する。
   * @param value 入力または選択された年月
   */
  onChange?: (
    value: { year: number; month: number; day: number } | null,
  ) => void;
  onBlur?: () => void;
} & ComponentStyleProps;

/**
 * 年月選択コンポーネント.
 * レイアウトはカスタムコンポーネントで設定する。
 *
 * yyyy/MM形式の年月が入力された場合は日付を返却する。
 * それ以外の場合はnullを返却。
 */
export const CMFormInputBaseDate = ({
  editMode = "editable",
  variant = "default",
  size = "md",

  value = null,

  changedFrom = null,

  icon = <FiCalendar />,
  placeholder,
  minDate,
  maxDate,

  onChange,
  onBlur,

  sx,
  ...rest
}: CMFormInputBaseDateProps) => {
  const styles = useMultiStyleConfig("CMFormInputBaseDateStyles", {
    variant,
    size,
  });
  const isDiffMode = useMemo(() => variant === "diff", [variant]);

  const disabled = useMemo(
    () => editMode === "disabled" || editMode === "readOnly",
    [editMode],
  );
  const isInvalid = useMemo(() => variant === "error", [variant]);

  /**
   * 差分モード時、値が変更前と異なるならtrue。
   * 以下のいずれかの場合はfalse。
   * - 差分モードではない。
   * - 変更前と値が同一である。
   */
  const hasChangedInDiffMode = useMemo(() => {
    if (!isDiffMode) return false;
    return !isEqual(value, changedFrom);
  }, [isDiffMode, value, changedFrom]);

  const inputStyles = useMemo(
    () => ({
      ...styles.containerBase,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
    }),
    [hasChangedInDiffMode, styles],
  );
  const readonlyStyles = useMemo(
    () => ({
      ...styles.readonly,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );

  const textValue: string = useMemo(
    () => (hasValue(value) ? ymdToText(value) : ""),
    [value],
  );

  return editMode === "readOnly" ? (
    <Text sx={readonlyStyles} {...rest}>
      {textValue}
    </Text>
  ) : (
    <CMDatePicker
      size={size}
      placeholder={placeholder}
      icon={icon}
      selected={value}
      disabled={disabled}
      isInvalid={isInvalid}
      minDate={minDate}
      maxDate={maxDate}
      onChange={onChange}
      onBlur={onBlur}
      inputStyles={inputStyles}
      {...rest}
    />
  );
};

export const CMFormInputBaseDateStyles: ComponentMultiStyleConfig = {
  parts: ["containerBase", "readonly", "diff"],
  baseStyle: {
    containerBase: {},
    readonly: {
      maxW: "max-content",
    },
    diff: {
      borderWidth: "1px",
      borderColor: "orange.500",
      _hover: {
        borderColor: "orange.500",
      },
      _focus: {
        _hover: {
          borderColor: "blue.500",
        },
      },
    },
  },
};
